import React, { useState } from 'react';
import { 
  LineChart, 
  Line, 
  BarChart,
  Bar,
  PieChart, 
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer 
} from 'recharts';
import { 
  Activity,
  HardDrive,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Database,
  RefreshCcw,
  Calendar,
  TrendingUp
} from 'lucide-react';

// Sample data for charts
const performanceData = [
  { date: '2024-01', success: 95, failed: 5, total: 120, avgDuration: 45 },
  { date: '2024-02', success: 92, failed: 8, total: 135, avgDuration: 48 },
  { date: '2024-03', success: 97, failed: 3, total: 142, avgDuration: 42 },
  { date: '2024-04', success: 94, failed: 6, total: 128, avgDuration: 46 },
  { date: '2024-05', success: 96, failed: 4, total: 138, avgDuration: 43 },
  { date: '2024-06', success: 93, failed: 7, total: 145, avgDuration: 47 }
];

const storageData = [
  { date: '2024-01', used: 250, available: 750 },
  { date: '2024-02', used: 320, available: 680 },
  { date: '2024-03', used: 450, available: 550 },
  { date: '2024-04', used: 520, available: 480 },
  { date: '2024-05', used: 580, available: 420 },
  { date: '2024-06', used: 620, available: 380 }
];

const backupTypeData = [
  { name: 'Full', value: 30 },
  { name: 'Incremental', value: 45 },
  { name: 'Differential', value: 25 }
];

const CustomCard = ({ children, className = '' }) => (
  <div className={`bg-gray-800 rounded-lg shadow-lg p-4 ${className}`}>
    {children}
  </div>
);

const StatCard = ({ icon: Icon, title, value, trend, className = '' }) => (
  <CustomCard className={`flex items-center ${className}`}>
    <div className="p-3 rounded-full bg-blue-500/20 mr-4">
      <Icon size={24} className="text-blue-500" />
    </div>
    <div>
      <h3 className="text-gray-400 text-sm">{title}</h3>
      <div className="flex items-center space-x-2">
        <p className="text-2xl font-semibold">{value}</p>
        {trend && (
          <span className={`text-sm ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
    </div>
  </CustomCard>
);

const BackupMonitoring = () => {
  const [timeRange, setTimeRange] = useState('1M');

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Backup Monitoring Dashboard</h1>
        <div className="flex space-x-4">
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded px-3 py-2"
          >
            <option value="1D">Last 24 Hours</option>
            <option value="1W">Last Week</option>
            <option value="1M">Last Month</option>
            <option value="3M">Last 3 Months</option>
            <option value="1Y">Last Year</option>
          </select>
          <button className="bg-blue-600 hover:bg-blue-700 rounded px-4 py-2 flex items-center">
            <RefreshCcw size={18} className="mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard 
          icon={CheckCircle} 
          title="Success Rate" 
          value="95.2%" 
          trend={2.1} 
        />
        <StatCard 
          icon={Clock} 
          title="Avg. Backup Duration" 
          value="45m" 
          trend={-5.3} 
        />
        <StatCard 
          icon={Database} 
          title="Total Data Backed Up" 
          value="1.2 TB" 
          trend={12.7} 
        />
        <StatCard 
          icon={Activity} 
          title="Active Jobs" 
          value="8" 
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Backup Performance Trend */}
        <CustomCard>
          <h2 className="text-lg font-semibold mb-4">Backup Performance Trend</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                  itemStyle={{ color: '#9CA3AF' }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="success" 
                  stroke="#10B981" 
                  strokeWidth={2}
                />
                <Line 
                  type="monotone" 
                  dataKey="failed" 
                  stroke="#EF4444" 
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CustomCard>

        {/* Storage Usage */}
        <CustomCard>
          <h2 className="text-lg font-semibold mb-4">Storage Usage Over Time</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={storageData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                  itemStyle={{ color: '#9CA3AF' }}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="used" 
                  stackId="1"
                  stroke="#60A5FA" 
                  fill="#60A5FA" 
                />
                <Area 
                  type="monotone" 
                  dataKey="available" 
                  stackId="1"
                  stroke="#374151" 
                  fill="#374151" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CustomCard>

        {/* Backup Types Distribution */}
        <CustomCard>
          <h2 className="text-lg font-semibold mb-4">Backup Types Distribution</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={backupTypeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                >
                  {backupTypeData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={['#60A5FA', '#34D399', '#F472B6'][index]} 
                    />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                  itemStyle={{ color: '#9CA3AF' }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CustomCard>

        {/* Backup Duration by Type */}
        <CustomCard>
          <h2 className="text-lg font-semibold mb-4">Average Backup Duration</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                  itemStyle={{ color: '#9CA3AF' }}
                />
                <Legend />
                <Bar 
                  dataKey="avgDuration" 
                  name="Duration (minutes)"
                  fill="#60A5FA" 
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CustomCard>
      </div>

      {/* Recent Activity */}
      <CustomCard className="mt-6">
        <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>
        <div className="space-y-4">
          {[
            { status: 'success', message: 'Full backup completed successfully', time: '5 minutes ago' },
            { status: 'warning', message: 'Incremental backup taking longer than usual', time: '1 hour ago' },
            { status: 'error', message: 'Backup failed for Database Server', time: '2 hours ago' },
            { status: 'success', message: 'Differential backup completed', time: '3 hours ago' },
          ].map((activity, index) => (
            <div key={index} className="flex items-center space-x-4 p-3 rounded bg-gray-800/50">
              {activity.status === 'success' && <CheckCircle className="text-green-500" />}
              {activity.status === 'warning' && <AlertCircle className="text-yellow-500" />}
              {activity.status === 'error' && <XCircle className="text-red-500" />}
              <div className="flex-1">
                <p className="text-sm">{activity.message}</p>
                <p className="text-xs text-gray-400">{activity.time}</p>
              </div>
            </div>
          ))}
        </div>
      </CustomCard>
    </div>
  );
};

export default BackupMonitoring;